import { useCallback } from "react";
import { trackGa4Event } from "@/analytics/ga4";
import { useApplication } from "@/contexts/application-context";
import { FormContextType, useFormContext } from "@/contexts/form";
import { useStepNavigation } from "@/contexts/step-navigation-context";
import { logError } from "@/datadog/logger";
import { useAboutForm } from "@/features/purpose/use-about-loan-form";
import {
	useCoApplicantContactSchema,
	useValidationCountry,
} from "@/hooks/form/use-contact-field-schemas";
import {
	emptyCoApplicant,
	useDebtPartialSchema,
	useEmploymentCarsPartialSchema,
	useEmploymentPartialSchema,
	useResidencePartialSchema,
} from "@/hooks/form/use-economy-form";
import {
	useCivilApplicantSchema,
	useContactApplicantSchema,
} from "@/hooks/form/use-personal-information-form";
import { useCountrySettings } from "@/hooks/use-app-country";
import { CacheForms } from "@/types/cache-forms";
import {
	StepType,
	getCoApplicantStepSection,
	getNextStep,
	getStepSequence,
} from "@/types/routes";
import { prepareCacheFormsForApi } from "@/utils/mapping/prepare-cache-forms-for-api";
import { setStep } from "@/utils/step-cookie";
import { areDuplicateEmails } from "@/utils/validation/duplicate-co-applicant-email";
import { areDuplicateNationalIds } from "@/utils/validation/duplicate-co-applicant-national-id";

type FormSnapshot = Required<CacheForms>;

type SafeParsable = { safeParse: (value: unknown) => { success: boolean } };

type SaveOptions = {
	validate?: (values: FormSnapshot) => boolean;
	prepare?: (forms: FormContextType) => void;
	nextStep: StepType;
};

const useFormSnapshot = () => {
	const forms = {
		landingPageForm: useFormContext("landingPageForm"),
		aboutForm: useFormContext("aboutForm"),
		personalInformationForm: useFormContext("personalInformationForm"),
		economyForm: useFormContext("economyForm"),
	};

	const getSnapshot = useCallback(
		(): FormSnapshot => ({
			landingPageForm: forms.landingPageForm.getValues(),
			aboutForm: forms.aboutForm.getValues(),
			personalInformationForm: forms.personalInformationForm.getValues(),
			economyForm: forms.economyForm.getValues(),
		}),
		[
			forms.aboutForm,
			forms.economyForm,
			forms.landingPageForm,
			forms.personalInformationForm,
		]
	);

	return { forms, getSnapshot };
};

export const useSaveApplication = () => {
	const { refreshStep } = useStepNavigation();
	const { applicationId } = useApplication();
	const { forms, getSnapshot } = useFormSnapshot();
	const validationCountry = useValidationCountry();

	return useCallback(
		async ({
			validate,
			prepare,
			nextStep,
		}: SaveOptions): Promise<boolean> => {
			prepare?.(forms);
			const values = getSnapshot();

			if (validate && !validate(values)) {
				return false;
			}

			try {
				const response = await fetch(
					`/api/db-operations/update-application/${applicationId}`,
					{
						method: "PUT",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({
							...prepareCacheFormsForApi(
								values,
								validationCountry
							),
							nextStep,
						}),
					}
				);

				if (!response.ok) {
					logError("Failed to save application", undefined, {
						task: "updateApplication",
						applicationId,
						statusCode: response.status,
						responseBody: await response.text(),
					});
					return false;
				}
			} catch (err) {
				logError("Failed to save application", err, {
					task: "updateApplication",
					applicationId,
				});
				return false;
			}

			setStep(nextStep);
			refreshStep();
			return true;
		},
		[applicationId, forms, getSnapshot, refreshStep, validationCountry]
	);
};

/** Next step in this country's flow; the co-applicant sub-flow implies a co-applicant. */
const useNextStep = (step: StepType): StepType | undefined => {
	const settings = useCountrySettings();
	return getNextStep(
		getStepSequence(
			settings,
			getCoApplicantStepSection(step) !== undefined
		),
		step
	);
};

const useAdvanceStep = (
	schema: SafeParsable,
	select: (values: FormSnapshot) => unknown,
	step: StepType
) => {
	const save = useSaveApplication();
	const nextStep = useNextStep(step);

	return useCallback(
		async () =>
			nextStep !== undefined &&
			save({
				validate: (values) =>
					schema.safeParse(select(values) ?? {}).success,
				nextStep,
			}),
		[nextStep, save, schema, select]
	);
};

const selectApplicantEconomy = (values: FormSnapshot) =>
	values.economyForm.applicant?.economy;
const selectCoApplicantEconomy = (values: FormSnapshot) =>
	values.economyForm.coApplicant?.economy;
const selectCoApplicantPerson = (values: FormSnapshot) =>
	values.economyForm.coApplicant?.applicant;
const selectAboutForm = (values: FormSnapshot) => values.aboutForm;
const selectApplicant = (values: FormSnapshot) =>
	values.personalInformationForm.applicant;

export const useAdvanceLanding = () => {
	const save = useSaveApplication();
	return useCallback(() => save({ nextStep: StepType.AboutLoan }), [save]);
};

export const useAdvanceAboutLoan = () =>
	useAdvanceStep(useAboutForm().schema, selectAboutForm, StepType.AboutLoan);

export const useAdvancePersonalInfoContact = () =>
	useAdvanceStep(
		useContactApplicantSchema(),
		selectApplicant,
		StepType.PersonalInformationContact
	);

export const useAdvancePersonalInfoCivil = () =>
	useAdvanceStep(
		useCivilApplicantSchema(),
		selectApplicant,
		StepType.PersonalInformationCivil
	);

export const useAdvanceEconomyEmployment = () =>
	useAdvanceStep(
		useEmploymentPartialSchema(),
		selectApplicantEconomy,
		StepType.EconomyEmployment
	);

export const useAdvanceEconomyCars = () =>
	useAdvanceStep(
		useEmploymentCarsPartialSchema(),
		selectApplicantEconomy,
		StepType.EconomyCars
	);

export const useAdvanceEconomyDebt = () =>
	useAdvanceStep(
		useDebtPartialSchema(),
		selectApplicantEconomy,
		StepType.EconomyDebt
	);

export const useAdvanceEconomyResidence = () =>
	useAdvanceStep(
		useResidencePartialSchema(),
		selectApplicantEconomy,
		StepType.EconomyResidence
	);

export const useAdvanceCoApplicantContact = () => {
	const save = useSaveApplication();
	const mainApplicant = useFormContext("personalInformationForm").watch(
		"applicant"
	);
	const mainNationalId = mainApplicant?.nationalId;
	const contactSchema = useCoApplicantContactSchema(mainApplicant);
	const validationCountry = useValidationCountry();

	return useCallback(
		() =>
			save({
				validate: (values) => {
					const coApplicant =
						values.economyForm.coApplicant?.applicant;

					if (
						areDuplicateNationalIds(
							validationCountry,
							mainNationalId,
							coApplicant?.nationalId
						) ||
						areDuplicateEmails(
							mainApplicant?.emailAddress,
							coApplicant?.emailAddress
						)
					) {
						return false;
					}

					return contactSchema.safeParse(coApplicant ?? {}).success;
				},
				nextStep: StepType.CoApplicantCivil,
			}),
		[
			contactSchema,
			mainApplicant?.emailAddress,
			mainNationalId,
			save,
			validationCountry,
		]
	);
};

export const useAdvanceCoApplicantCivil = () =>
	useAdvanceStep(
		useCivilApplicantSchema(),
		selectCoApplicantPerson,
		StepType.CoApplicantCivil
	);

export const useAdvanceCoApplicantEmployment = () =>
	useAdvanceStep(
		useEmploymentPartialSchema(),
		selectCoApplicantEconomy,
		StepType.CoApplicantEmployment
	);

export const useAdvanceCoApplicantCars = () =>
	useAdvanceStep(
		useEmploymentCarsPartialSchema(),
		selectCoApplicantEconomy,
		StepType.CoApplicantCars
	);

export const useAdvanceCoApplicantDebt = () =>
	useAdvanceStep(
		useDebtPartialSchema(),
		selectCoApplicantEconomy,
		StepType.CoApplicantDebt
	);

export const useAdvanceCoApplicantResidence = () =>
	useAdvanceStep(
		useResidencePartialSchema(),
		selectCoApplicantEconomy,
		StepType.CoApplicantResidence
	);

export const useAdvanceCoApplicantChoice = () => {
	const save = useSaveApplication();
	const economyForm = useFormContext("economyForm");
	const settings = useCountrySettings();

	return useCallback(
		async (selectedHasCoApplicant?: boolean): Promise<boolean> => {
			const hadCoApplicant =
				economyForm.getValues().hasCoApplicant === true;
			const hasCoApplicant =
				selectedHasCoApplicant ??
				economyForm.getValues().hasCoApplicant;

			if (typeof hasCoApplicant !== "boolean") {
				return false;
			}

			const saved = await save({
				prepare: (forms) => {
					forms.economyForm.setValue(
						"hasCoApplicant",
						hasCoApplicant
					);
					if (!hasCoApplicant) {
						forms.economyForm.setValue(
							"coApplicant",
							emptyCoApplicant(settings.validationCountry)
						);
					}
				},
				nextStep:
					getNextStep(
						getStepSequence(settings, hasCoApplicant),
						StepType.CoApplicantChoice
					) ?? StepType.Summary,
			});
			if (saved && hasCoApplicant && !hadCoApplicant) {
				trackGa4Event("co_applicant_added");
			}
			return saved;
		},
		[economyForm, save, settings]
	);
};
